import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import footerLogo from '@repo/common/assets/logos/footer_logo.svg';
import {
  applyTypography,
  BaseButton,
  LEGAL_LINKS,
  NAV_GROUPS,
  MOBILE_NAV_ITEMS,
  Section,
  Content,
  MainRow,
  DecorColumn,
  DecorPanel,
  HeroColumn,
  Hero,
  TitleRow,
  MobileShortcuts,
  LinkRow,
  LinkGroup,
  LinkList,
  FooterBottomRow as BottomRow,
  LegalNav,
  useLang,
  useT,
} from '@repo/common';

const cx = (...classes) => classes.filter(Boolean).join(' ');

const CTA_COPY = {
  headline: 'O!ng Lab',
  description: 'empowers global talents to build sustainable careers in Korea.',
  cta: {
    label: 'contact us',
    href: 'mailto:hello@oinglab.com',
  },
};

const Footer = () => {
  const t = useT();
  const { lang } = useLang();
  const location = useLocation();
  const isMyRewardsPage =
    location.pathname === '/community/kpathport/myrewards';
  const bgClassName = isMyRewardsPage
    ? 'bg-green-light01'
    : 'bg-grayscale-gray01';
  const titleBase = applyTypography('b20Strong', CTA_COPY.headline);

  const introBase = applyTypography('b20Strong', CTA_COPY.description);

  const ctaTypo = applyTypography('b16Strong', CTA_COPY.cta.label);
  const mobileShortcutTypo = applyTypography('b18Strong');
  const mobileShortcutAccent = 'text-green-deep02';

  const linkHeadingBase = applyTypography('b12');
  const linkItemBase = applyTypography('b12');
  const bottomCopyBase = applyTypography('b12');

  return (
    <Section className={bgClassName}>
      <Content>
        <MainRow>
          <DecorColumn aria-hidden='true'>
            <DecorPanel>
              <img
                src={footerLogo}
                alt=''
                loading='lazy'
                className='absolute inset-0 object-contain w-full h-full pointer-events-none mix-blend-screen'
              />
            </DecorPanel>
          </DecorColumn>

          <MobileShortcuts>
            {MOBILE_NAV_ITEMS.map(({ label, href, external, accent }) => (
              <a
                key={label}
                href={href}
                target={external ? '_blank' : undefined}
                rel={external ? 'noreferrer noopener' : undefined}
                className={cx(
                  'inline-flex items-center justify-center px-2 py-3 no-underline',
                  mobileShortcutTypo,
                  'tracking-[-0.01em] text-text-inverse motion-reduce:transition-none transition-colors duration-200 ease-linear',
                  accent ? mobileShortcutAccent : '',
                  accent ? '' : 'hover:text-green-deep02',
                )}
                data-accent={accent ? 'true' : undefined}
              >
                {t(label)}
              </a>
            ))}
          </MobileShortcuts>

          <HeroColumn>
            <Hero>
              <TitleRow>
                <span
                  className={cx(
                    titleBase,
                    'text-grayscale-gray03',
                    'tabletH:text-h7',
                    'desktopL:text-h6',
                    'desktopXXL:text-h5',
                  )}
                >
                  {CTA_COPY.headline}
                </span>
                <span
                  aria-hidden='true'
                  className='ml-1 leading-none translate-y-1 text-grayscale-gray03'
                >
                  &reg;
                </span>
              </TitleRow>
              <p
                className={cx(
                  'mb-0 max-w-[308px] text-grayscale-gray03',
                  introBase,
                  'tabletH:text-h6',
                  'desktopXXL:text-h5',
                  'mobile:max-w-[362px]',
                  'tabletH:max-w-[388px]',
                  'tabletV:max-w-[386px]',
                  'desktopL:max-w-[519px]',
                  'desktopXXL:max-w-[571px]',
                )}
              >
                {t(CTA_COPY.description)}
              </p>
              <BaseButton
                as='a'
                href={CTA_COPY.cta.href}
                type='text'
                size='xl'
                hideIcon
                rounded='pill'
                textclass={cx('no-underline', ctaTypo)}
                textWrapperClass='text-center'
                className={cx(
                  'mt-5 w-full max-w-[308px] min-h-[52px]',
                  'mobile:max-w-[362px] mobile:min-h-[48px]',
                  'tabletH:max-w-[388px] tabletH:min-h-[48px] tabletH:mt-6',
                  'tabletV:max-w-[386px] tabletV:min-h-[48px] tabletV:mt-4',
                  'desktopL:max-w-[519px]',
                  'desktopXXL:max-w-[571px]',
                )}
              >
                {t(CTA_COPY.cta.label)}
              </BaseButton>
            </Hero>

            <LinkRow>
              {NAV_GROUPS.map((group) => {
                const isSocial = group.title === 'Social';
                return (
                  <LinkGroup key={group.title} hiddenOnMobile>
                    <h3
                      className={cx(
                        'm-0 text-grayscale-gray06 tracking-[0.01em]',
                        linkHeadingBase,
                        'tabletV:text-b14',
                      )}
                    >
                      {t(group.title)}
                    </h3>
                    <LinkList>
                      {group.items.map((item) => {
                        const accent = item.accent;
                        return (
                          <li key={item.label} className='flex'>
                            <a
                              href={item.href}
                              target={item.external ? '_blank' : undefined}
                              rel={
                                item.external
                                  ? 'noreferrer noopener'
                                  : undefined
                              }
                              className={cx(
                                'inline-flex items-center min-h-[35px] py-2 no-underline transition-colors duration-[250ms] ease-linear motion-reduce:transition-none',
                                linkItemBase,
                                isSocial
                                  ? 'mobile:text-b14Strong'
                                  : 'tabletH:text-b16Strong desktopXXL:text-b18Strong',
                                'tabletH:py-[6px] desktopXXL:py-2',
                                'text-text-inverse tracking-[-0.01em] hover:text-green-deep02',
                                accent
                                  ? 'text-green-deep02 hover:text-green-deep02'
                                  : '',
                              )}
                            >
                              {t(item.label)}
                            </a>
                          </li>
                        );
                      })}
                    </LinkList>
                  </LinkGroup>
                );
              })}
            </LinkRow>
          </HeroColumn>
        </MainRow>

        <BottomRow>
          <p
            className={cx(
              'm-0 text-grayscale-gray05',
              bottomCopyBase,
              'desktopXXL:text-b16',
            )}
          >
            &copy; 2025 O!ng Lab. {t('All right reserved.', '모든 권리 보유.')}
          </p>
          <LegalNav aria-label='Legal'>
            {LEGAL_LINKS.map((link) => {
              const isExternal = /^(https?:|mailto:)/.test(link.href);
              const linkClass =
                'no-underline transition-colors duration-200 ease-linear text-grayscale-gray05 hover:text-text-inverse motion-reduce:transition-none cursor-pointer';
              return (
                <li key={link.label}>
                  {isExternal ? (
                    <a
                      href={(lang === 'kr' && link.hrefKr) || link.href}
                      target={link.external ? '_blank' : undefined}
                      rel={link.external ? 'noreferrer noopener' : undefined}
                      className={linkClass}
                    >
                      {t(link.label)}
                    </a>
                  ) : (
                    <Link to={link.href} className={linkClass}>
                      {t(link.label)}
                    </Link>
                  )}
                </li>
              );
            })}
          </LegalNav>
        </BottomRow>
      </Content>
    </Section>
  );
};

export default Footer;
